import dayjs from "dayjs";

import { CreateSaleInput } from "../../backend/generated/create-sale-input";
import { GetSaleInput } from "../../backend/generated/get-sale-input";
import { Sale } from "../../backend/generated/sale-model";
import { AssetTypes, ValidationMessages } from "../../backend/types/utils";
import { randomAdjective, randomDate, randomNoun, randomNumber, randomSelectOption, randomString } from "../../utils/random";
import { prune } from "../../utils/utility";
import { CustomValidationArguments, validateDates, validateNumbersInRange, validateRequiredNotDefault } from "../../utils/validation";

export class SaleManageable extends Sale {
  constructor(obj: any) {
    super();

    obj && Object.assign(
      this,
      prune(obj),
      { assetType: obj.assetType ? obj.assetType.toLowerCase() as AssetTypes : obj.assetType }
    );

    const investments = [];
    if (this.investments) {
      for (let i = 0; i < this.investments.length; i++) {
        investments.push(this.investments[i]);
      }
    }
    this.investments = investments;
  }

  get percentageFunded(): number {
    const factor = 100;
    return Math.round((100 - this.sharesAvailable * 100 / this.sharesCap) * factor) / factor;
  }

  get sharesReserved(): number {
    return this.sharesSold + this.sharesLocked;
  }

  get sharesAvailable(): number {
    return this.sharesCap - this.sharesReserved;
  }

  static default(): any {
    return { ...new Sale() }
  }
}

export class CreateSaleManageable extends CreateSaleInput {
  filesObj?: Array<File>;

  static readonly minFee: number = 0;
  static readonly minInterestRate: number = 0;
  static readonly minMinPurchase: number = 1;
  static readonly minMaxPurchase: number = 1;
  static readonly minSharesCap: number = 1;

  constructor(obj?: any) {
    super();
    if (obj) {
      obj && Object.assign(
        this,
        CreateSaleManageable.default(),
        prune(obj)
      );
    } else {
      Object.assign(
        this,
        CreateSaleManageable.default()
      );
    }
  }

  static readonly required = {
    assetType: true,
    baFinId: true,
    company: true,
    description: true,
    endDate: true,
    fee: true,
    interestRate: true,
    interestPayoutPeriod: true,
    maxPurchase: true,
    minPurchase: true,
    name: true,
    sharesCap: true,
    startDate: true,
  };

  static default(): any {
    return {
      ...new CreateSaleInput(),
      assetType: "",
      endDate: "",
      fee: "",
      interestRate: "",
      interestPayoutPeriod: "",
      maxPurchase: "",
      minPurchase: "",
      sharesCap: "",
      startDate: "",
      images: [],
      filesObj: [],
    }
  }

  static automatic(): any {
    const today = new Date();

    let maxDate = new Date();
    maxDate.setFullYear(maxDate.getFullYear() + 2);

    const startDate = new Date(randomDate(
      today,
      maxDate
    ));

    let minEndDate = new Date(Number(startDate));
    minEndDate.setDate(startDate.getDate() + 1);

    return {
      ...CreateSaleManageable.default(),
      assetType: randomSelectOption('asset'),
      name: randomAdjective() + ' ' + randomNoun(),
      company: randomAdjective() + ' ' + randomNoun(),
      description: "X",
      startDate: dayjs(startDate).toISOString(),
      endDate: dayjs(randomDate(
        minEndDate,
        maxDate
      )).toISOString(),
      minPurchase: randomNumber(
        1,
        900
      ),
      maxPurchase: 1000,
      sharesCap: randomNumber(
        2000,
        100000
      ),
      fee: randomNumber(
        1,
        30
      ),
      interestRate: randomNumber(
        1,
        70
      ),
      interestPayoutPeriod: randomSelectOption('period'),
      baFinId: randomString(6),
    };
  }

  validate(
    keys: Array<string>, messages: any
  ): any {
    const shouldValidate = (key: string): boolean => {
      return keys.includes(key);
    };

    let errors: any = {};

    validateRequiredNotDefault(
      errors,
      CreateSaleManageable.required,
      this,
      CreateSaleManageable.default(),
      keys
    );

    validateNumbersInRange(
      [{
        key: 'interestRate',
        min: CreateSaleManageable.minInterestRate,
      }, {
        key: 'fee',
        min: CreateSaleManageable.minFee,
      }, {
        key: 'sharesCap',
        min: CreateSaleManageable.minSharesCap,
      }, {
        key: 'minPurchase',
        min: CreateSaleManageable.minMinPurchase,
      }, {
        key: 'maxPurchase',
        min: CreateSaleManageable.minMaxPurchase,
      }],
      errors,
      this,
      keys,
      messages[ValidationMessages.MINIMUM],
      messages[ValidationMessages.MAXIMUM],
    );

    errors = Object.assign(
      errors,
      validateDates(new CustomValidationArguments({
        keys: ['startDate', 'endDate'],
        errors,
        values: this,
        allowedKeys: keys,
        errorMessage: messages[ValidationMessages.REQUIRED]
      }))
    );

    if (!errors.startDate &&
        !errors.endDate &&
        shouldValidate('startDate') &&
        shouldValidate('endDate') &&
        (!dayjs(this.startDate).isBefore(dayjs(this.endDate)) ||
        dayjs().isAfter(this.startDate))) {
      errors.startDate = messages[ValidationMessages.INVALID];
      errors.endDate = messages[ValidationMessages.INVALID];
    }

    return errors;
  }

  serialize(): any {
    let data: any = Object.assign(
      {},
      this
    );

    if (dayjs(data.startDate).isValid()) {
      data.startDate = dayjs(data.startDate).toISOString();
    }

    if (dayjs(data.endDate).isValid()) {
      data.endDate = dayjs(data.endDate).toISOString();
    }

    data.assetType = data.assetType.toUpperCase();
    data.interestPayoutPeriod = data.interestPayoutPeriod.toUpperCase();

    data.fee = parseFloat(data.fee);
    data.interestRate = parseFloat(data.interestRate);

    delete data.filesObj;

    return data;
  }
}

export class GetSaleManageable extends GetSaleInput {
  constructor(obj?: any) {
    super();
    if (obj) {
      obj && Object.assign(
        this,
        GetSaleManageable.default(),
        prune(obj)
      );
    } else {
      Object.assign(
        this,
        GetSaleManageable.default()
      );
    }
  }

  static readonly required = {};

  static default(): any {
    return { ...new GetSaleInput() }
  }

  validate(keys: Array<string>): any {
    return validateRequiredNotDefault(
      {},
      CreateSaleManageable.required,
      this,
      CreateSaleManageable.default(),
      keys
    );
  }

  serialize(): any {
    let data: any = Object.assign(
      {},
      this
    );

    return data;
  }
}
